import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {Pagination} from 'lib/Listings';
import {GLOBAL_SEARCH_URL} from 'search/SearchConstants';
import * as net from 'util/Net';

import {STATUS_CODE_TO_STATUS} from '../runs/PredictionBatchTypes';
import type {SortDirection} from './listingTypes';
import type {JobStatus} from './StatusBadge';
import {useVisibilityAwarePolling} from './useVisibilityAwarePolling';

export interface BatchRow {
  createdAt: string;
  id: string;
  modelName: string;
  modifiedAt: string;
  name: string;
  status: JobStatus;
}

type BatchSortKey = 'createdAt' | 'modifiedAt' | 'name';

interface UseBatchesSearchParams {
  limit: number;
  offset: number;
  query: string;
  sort: {direction: SortDirection; key: BatchSortKey};
}

export interface UseBatchesSearchResult {
  batches: Array<BatchRow>;
  error: string | null;
  isLoading: boolean;
  pagination: Pagination;
  refresh: () => void;
}

const POLL_INTERVAL_MS = 30_000;

const SORT_KEY_MAP: Record<BatchSortKey, string> = {
  createdAt: 'created_at',
  modifiedAt: 'modified_at',
  name: 'name',
};

interface PredictionBatchListingData {
  createdAt: string;
  creator?: {name: string} | null;
  id: string;
  modelName: string;
  modifiedAt: string;
  name: string;
  statusCode: string;
}

interface SearchResponse {
  ids: Array<string>;
  isExact: boolean;
  predictionBatchesById?: Record<string, PredictionBatchListingData>;
  total: number;
}

function mapToBatchRow(id: string, listing: PredictionBatchListingData | undefined): BatchRow {
  if (!listing) {
    return {id, name: id, status: 'PENDING', modelName: '', createdAt: '', modifiedAt: ''};
  }
  return {
    createdAt: listing.createdAt ?? '',
    id: listing.id,
    modelName: listing.modelName ?? '',
    modifiedAt: listing.modifiedAt ?? '',
    name: listing.name ?? id,
    status: STATUS_CODE_TO_STATUS[listing.statusCode] ?? 'PENDING',
  };
}

export default function useBatchesSearch({
  query,
  sort,
  offset,
  limit,
}: UseBatchesSearchParams): UseBatchesSearchResult {
  const [batches, setBatches] = React.useState<Array<BatchRow>>([]);
  const [pagination, setPagination] = React.useState<Pagination>({offset: 0, limit, total: 0});
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = React.useState(0);
  const isFetchingRef = React.useRef(false);
  const isBackgroundRefreshRef = React.useRef(false);

  const refresh = React.useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  // Poll regardless of row statuses: new batches can be submitted from elsewhere at any time, and a
  // failed first load must be able to recover. A tick is skipped while a request is outstanding so a
  // poll never supersedes a user-initiated load.
  const poll = React.useCallback(() => {
    if (isFetchingRef.current) {
      return;
    }
    isBackgroundRefreshRef.current = true;
    setRefreshCounter((prev) => prev + 1);
  }, []);
  useVisibilityAwarePolling(true, POLL_INTERVAL_MS, poll);

  React.useEffect(() => {
    let cancelled = false;
    // Background refreshes keep the current rows, or error, on screen until a response replaces them.
    const isBackgroundRefresh = isBackgroundRefreshRef.current;
    isBackgroundRefreshRef.current = false;
    isFetchingRef.current = true;
    if (!isBackgroundRefresh) {
      setIsLoading(true);
      setError(null);
    }

    const searchParams = {
      filters: [{key: 'TYPES', operator: 'IS_ONE_OF', value: [ModelTypeConstants.PREDICTION_BATCH]}],
      limit,
      offset,
      query,
      sorts: [{key: SORT_KEY_MAP[sort.key], reverse: sort.direction === 'desc'}],
    };

    net
      .postJSONPromise({url: GLOBAL_SEARCH_URL, data: searchParams})
      .then((response: SearchResponse) => {
        if (cancelled) {
          return;
        }
        isFetchingRef.current = false;
        const predictionBatchesById = response.predictionBatchesById ?? {};
        setBatches(response.ids.map((id) => mapToBatchRow(id, predictionBatchesById[id])));
        setPagination({offset, limit, total: response.total, isExact: response.isExact});
        setError(null);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        isFetchingRef.current = false;
        // A transient poll failure leaves the page as it was; the next poll retries.
        if (isBackgroundRefresh) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Search failed');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query, sort.key, sort.direction, offset, limit, refreshCounter]);

  return {batches, pagination, isLoading, error, refresh};
}
